import { useCallback, useMemo, useState } from 'react';
import { useBackendAITrading } from '../contexts/BackendAITradingContext';
import { useAITrading } from '../contexts/AITradingContext';

export interface UnifiedAIState {
  isRunning: boolean;
  canStart: boolean;
  needsSetup: boolean;
  reason?: string;
}

export interface UnifiedAIHandlers {
  handleStartStop: () => Promise<void>;
  handleStartAI: () => Promise<void>;  // NEW: Separate start handler
  handleStopAI: () => Promise<void>;   // NEW: Separate stop handler
  handleShowSettings: () => void;
  handleCloseSettings: () => void;
  handleSaveStrategy: (strategyConfig: any) => Promise<void>;
  handleRemoveStrategy: () => Promise<void>;
}

export interface UseUnifiedAITradingReturn {
  aiState: UnifiedAIState;
  handlers: UnifiedAIHandlers;
  showSettings: boolean;
  setShowSettings: (show: boolean) => void;
  currentFirestoreStrategy: any;
  currentAssetConfig: any;
  assetAIConfig: any;
  refreshTrigger: number; // Expose refresh trigger for component re-renders
}

export const useUnifiedAITrading = (
  symbol: string,
  onSettingsOpen?: () => void,
  onSettingsClose?: () => void
): UseUnifiedAITradingReturn => {
  const { 
    getAssetAIConfig,
    isAssetAIEnabled,
    setAssetAIEnabled,
    stopAssetAITrading,
    removeStrategyCompletely,
    saveAssetStrategy,
    getAssetStrategies,
    activeStrategies,
    loadAITradingData
  } = useBackendAITrading();
  
  // Add refresh trigger to force re-renders after operations
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [forceUpdate, setForceUpdate] = useState(false);
  
  const { 
    getStrategy, 
    startStrategy, 
    stopStrategy 
  } = useAITrading();

  // Get current configurations
  const currentAssetConfig = getStrategy(symbol);
  const assetAIConfig = getAssetAIConfig(symbol);
  const assetStrategies = getAssetStrategies(symbol);
  const isCurrentAssetAIEnabled = isAssetAIEnabled(symbol);

  // Get current strategy from Firestore activeStrategies
  const currentFirestoreStrategy = useMemo(() => {
    let strategy = activeStrategies.find((strategy: any) => {
      const matchesSymbol = strategy?.symbol === symbol;
      const hasAiSelectedStrategy = !!(strategy?.aiSelectedStrategy && strategy.aiSelectedStrategy.length > 0);
      return typeof strategy === 'object' && strategy !== null && 
             matchesSymbol && hasAiSelectedStrategy;
    });
    
    if (!strategy) {
      strategy = activeStrategies.find((strategy: any) => {
        const matchesSymbol = strategy?.symbol === symbol;
        return typeof strategy === 'object' && strategy !== null && matchesSymbol;
      });
    }
    
    return strategy;
  }, [activeStrategies, symbol, refreshTrigger, forceUpdate]);

  // Determine AI state - UNIFIED LOGIC
  const aiState = useMemo((): UnifiedAIState => {
    // console.log(`🔍 Unified AI: Computing aiState for ${symbol}`, {
    //   isCurrentAssetAIEnabled,
    //   assetAIConfig: assetAIConfig?.activeStrategyInstance,
    //   assetStrategiesLength: assetStrategies.length,
    //   currentFirestoreStrategy,
    //   currentAssetConfigStrategy: currentAssetConfig.strategy,
    //   refreshTrigger,
    //   forceUpdate,
    //   // Additional debug info
    //   assetAIConfigEnabled: assetAIConfig?.enabled,
    //   activeStrategyIsActive: assetAIConfig?.activeStrategyInstance?.isActive,
    //   activeStrategyAiTradeStartEnabled: assetAIConfig?.activeStrategyInstance?.aiTradeStartEnabled
    // });
    
    // Check if asset has AI trading enabled
    if (!isCurrentAssetAIEnabled) {
      return { isRunning: false, canStart: false, needsSetup: true, reason: 'AI trading disabled for this asset' };
    }
    
    // Check if asset has any strategies configured
    if (!assetAIConfig || assetStrategies.length === 0) {
      return { isRunning: false, canStart: false, needsSetup: true, reason: 'No strategies configured for this asset' };
    }
    
    // Check if there's an active strategy instance
    const activeStrategy = assetAIConfig.activeStrategyInstance;
    if (!activeStrategy) {
      return { isRunning: false, canStart: true, needsSetup: false, reason: 'Strategy needs to be activated' };
    }
    
    // Check if the strategy is currently running
    const isStrategyActive = activeStrategy.isActive === true && activeStrategy.aiTradeStartEnabled === true;
    
    if (isStrategyActive) {
      return { isRunning: true, canStart: false, needsSetup: false };
    } else {
      return { isRunning: false, canStart: true, needsSetup: false };
    }
    
    // Fallback to legacy logic for backward compatibility
    if (currentFirestoreStrategy && typeof currentFirestoreStrategy === 'object') {
      const strategy = currentFirestoreStrategy as any;
      const { isActive, aiTradeStartEnabled, aiSelectedStrategy } = strategy;
      const strategyIsSelected = aiTradeStartEnabled === true || (aiSelectedStrategy && aiSelectedStrategy.length > 0);
      
      if (isActive && strategyIsSelected) {
        return { isRunning: true, canStart: false, needsSetup: false };
      } else if (!isActive && strategyIsSelected) {
        return { isRunning: false, canStart: true, needsSetup: false };
      }
    }
    
    // Check if we have a strategy configured in the legacy system
    if (currentAssetConfig.strategy) {
      return { isRunning: false, canStart: true, needsSetup: false };
    }
    
    // Default: needs setup
    return { isRunning: false, canStart: false, needsSetup: true, reason: 'Configuration needed' };
  }, [isCurrentAssetAIEnabled, assetAIConfig, assetStrategies.length, currentFirestoreStrategy, currentAssetConfig.strategy, refreshTrigger, forceUpdate]);

  // SEPARATE START AI HANDLER - STARTS AI OR OPENS SETTINGS IF NO STRATEGY
  const handleStartAI = useCallback(async () => {
    // console.log(`🚀 Unified AI: Starting AI for ${symbol}`);
    
    // Check if we have a strategy selected
    const hasStrategy = aiState.canStart || (currentFirestoreStrategy as any)?.aiTradeStartEnabled;
    
    if (!hasStrategy) {
      // console.log(`⚙️ Unified AI: No strategy selected, opening settings for ${symbol}`);
      onSettingsOpen?.();
      return;
    }
    
    try {
      // Clear manual stop flag when starting AI (user wants to start AI)
      localStorage.removeItem('aiManualStop');
      // console.log(`🚀 Unified AI: Manual stop flag cleared - AI can be started`);
      
      // Enable AI trading for this asset
      await setAssetAIEnabled(symbol, true);
      
      // Update strategy to mark as active - handle both new and legacy systems
      if (assetAIConfig?.activeStrategyInstance) {
        const updatedStrategy = {
          ...assetAIConfig.activeStrategyInstance,
          isActive: true,
          aiTradeStartEnabled: true
        };
        await saveAssetStrategy(symbol, updatedStrategy);
      } else if (currentFirestoreStrategy) {
        const updatedStrategy = {
          ...currentFirestoreStrategy,
          isActive: true,
          aiTradeStartEnabled: true
        };
        await saveAssetStrategy(symbol, updatedStrategy);
      }
      
      // Start legacy method for backward compatibility
      if (currentAssetConfig.strategy) {
        startStrategy(symbol);
      }
      
      // Force reload AI trading data from Firestore to get latest state
      try {
        await loadAITradingData();
        // console.log(`🔄 Unified AI: AI trading data reloaded from Firestore for ${symbol}`);
        
        // Small delay to ensure state updates are processed
        await new Promise(resolve => setTimeout(resolve, 100));
        // console.log(`🔄 Unified AI: State update delay completed for ${symbol}`);
      } catch (error) {
        console.warn(`⚠️ Unified AI: Could not reload AI data:`, error);
      }
      
      // Force re-render
      setRefreshTrigger(prev => prev + 1);
      setForceUpdate(prev => !prev);
      
      // console.log(`✅ Unified AI: AI started successfully for ${symbol}`);
    } catch (error) {
      console.error(`❌ Unified AI: Error starting AI for ${symbol}:`, error);
    }
  }, [symbol, setAssetAIEnabled, assetAIConfig, currentFirestoreStrategy, saveAssetStrategy, currentAssetConfig, startStrategy, aiState.canStart, onSettingsOpen]);

  // SEPARATE STOP AI HANDLER - ONLY STOPS AI
  const handleStopAI = useCallback(async () => {
    // console.log(`⏹️ Unified AI: Stopping AI for ${symbol}`, {
    //   currentFirestoreStrategy: currentFirestoreStrategy?.isActive,
    //   assetAIConfig: assetAIConfig?.activeStrategyInstance?.isActive,
    //   aiState: aiState.isRunning
    // });
    
    try {
      // 1. Set manual stop flag immediately to prevent any auto-restart
      localStorage.setItem('aiManualStop', 'true');
      // console.log(`🛑 Unified AI: Manual stop flag set to prevent auto-restart`);
      
      // 2. Stop backend AI trading service immediately
      try {
        const { backendAIService } = await import('../services/backendAIService');
        const stopResult = await backendAIService.stopService();
        // console.log(`🛑 Unified AI: Backend service stop result:`, stopResult);
      } catch (backendError) {
        // Only log in development mode to avoid console spam
        if (process.env.NODE_ENV === 'development') {
          console.warn(`⚠️ Unified AI: Backend service stop failed (service might not be running):`, backendError);
        }
      }
      
      // 3. Stop AI trading (update Firestore flags) - This sets ALL flags to false
      await stopAssetAITrading(symbol);
      
      // 4. Stop legacy method for backward compatibility
      if (currentAssetConfig.isRunning) {
        stopStrategy(symbol);
      }
      
      // 5. Force reload AI trading data from Firestore to get latest state
      try {
        await loadAITradingData();
        // console.log(`🔄 Unified AI: AI trading data reloaded from Firestore for ${symbol}`);
        
        // Small delay to ensure state updates are processed
        await new Promise(resolve => setTimeout(resolve, 100));
        // console.log(`🔄 Unified AI: State update delay completed for ${symbol}`);
      } catch (error) {
        console.warn(`⚠️ Unified AI: Could not reload AI data:`, error);
      }
      
      // 6. Force re-render
      setRefreshTrigger(prev => {
        const newValue = prev + 1;
        // console.log(`🔄 Unified AI: Incrementing refresh trigger from ${prev} to ${newValue} for ${symbol} (stop)`);
        return newValue;
      });
      setForceUpdate(prev => {
        const newValue = !prev;
        // console.log(`🔄 Unified AI: Toggling forceUpdate from ${prev} to ${newValue} for ${symbol} (stop)`);
        return newValue;
      });
      
      // console.log(`✅ Unified AI: AI stopped successfully for ${symbol} - ALL flags set to false, manual stop flag set`);
    } catch (error) {
      console.error(`❌ Unified AI: Error stopping AI for ${symbol}:`, error);
    }
  }, [symbol, stopAssetAITrading, currentAssetConfig, stopStrategy, aiState]);

  // UNIFIED START/STOP HANDLER
  const handleStartStop = useCallback(async () => {
    // console.log(`🎯 Unified AI: handleStartStop called for ${symbol}`, {
    //   aiState,
    //   isCurrentAssetAIEnabled,
    //   assetAIConfig: assetAIConfig?.activeStrategyInstance,
    //   currentFirestoreStrategy
    // });
    
    if (aiState.needsSetup) {
      // console.log(`🔧 Unified AI: Opening settings popup for ${symbol} - needsSetup: true`);
      onSettingsOpen?.();
      return;
    }
    
    if (!aiState.canStart && !aiState.isRunning) {
      // console.log(`❌ Unified AI: Cannot start/stop for ${symbol} - canStart: ${aiState.canStart}, isRunning: ${aiState.isRunning}`);
      return;
    }
    
    try {
      if (aiState.isRunning) {
        // Stop AI trading - use the dedicated stop handler
        await handleStopAI();
        
      } else if (aiState.canStart) {
        // Start AI trading - use the dedicated start handler
        await handleStartAI();
      }
      
    } catch (error) {
      console.error(`❌ Unified AI: Error in start/stop AI for ${symbol}:`, error);
    }
  }, [
    symbol, 
    aiState, 
    handleStartAI,
    handleStopAI,
    onSettingsOpen
  ]);

  // UNIFIED STRATEGY HANDLERS
  const handleSaveStrategy = useCallback(async (strategyConfig: any) => {
    try {
      await setAssetAIEnabled(symbol, true);
      await saveAssetStrategy(symbol, strategyConfig);
      
      // Force re-render to update UI immediately
      setRefreshTrigger(prev => {
        const newValue = prev + 1;
        // console.log(`🔄 Unified AI: Incrementing refresh trigger from ${prev} to ${newValue} for ${symbol} (save)`);
        return newValue;
      });
      
      // Also toggle forceUpdate to ensure re-render
      setForceUpdate(prev => {
        // console.log(`🔄 Unified AI: Toggling forceUpdate from ${prev} to ${!prev} for ${symbol} (save)`);
        return !prev;
      });
      
      onSettingsClose?.();
    } catch (error) {
      console.error(`❌ Unified AI: Error saving strategy for ${symbol}:`, error);
    }
  }, [symbol, saveAssetStrategy, setAssetAIEnabled, onSettingsClose]);

  const handleRemoveStrategy = useCallback(async () => {
    try {
      // console.log(`🗑️ Unified AI: Starting strategy removal for ${symbol}`);
      
      // First, explicitly disable AI trading for this asset
      await setAssetAIEnabled(symbol, false);
      // console.log(`✅ Unified AI: Disabled AI trading for ${symbol}`);
      
      // Then use complete removal to remove strategy from activeStrategies array
      await removeStrategyCompletely(symbol);
      
      // console.log(`✅ Unified AI: Strategy removal completed for ${symbol}, triggering refresh`);
      
      // Force re-render immediately after removal
      setRefreshTrigger(prev => {
        const newValue = prev + 1;
        // console.log(`🔄 Unified AI: Incrementing refresh trigger from ${prev} to ${newValue} for ${symbol}`);
        return newValue;
      });
      
      // Also toggle forceUpdate to ensure re-render
      setForceUpdate(prev => {
        //  console.log(`🔄 Unified AI: Toggling forceUpdate from ${prev} to ${!prev} for ${symbol}`);
        return !prev;
      });
      
      onSettingsClose?.();
    } catch (error) {
      console.error(`❌ Unified AI: Error removing strategy for ${symbol}:`, error);
    }
  }, [symbol, removeStrategyCompletely, setAssetAIEnabled, onSettingsClose]);

  return {
    aiState,
    handlers: {
      handleStartStop,
      handleStartAI,  // NEW: Separate start handler
      handleStopAI,  // NEW: Separate stop handler
      handleShowSettings: onSettingsOpen || (() => {}),
      handleCloseSettings: onSettingsClose || (() => {}),
      handleSaveStrategy,
      handleRemoveStrategy
    },
    showSettings: false, // This will be managed by individual components
    setShowSettings: () => {}, // This will be managed by individual components
    currentFirestoreStrategy,
    currentAssetConfig,
    assetAIConfig,
    refreshTrigger // Expose refresh trigger for component re-renders
  };
};
